import {
  ToggleGroupItem,
  ToggleGroupRoot,
  useAccessControl,
} from '@plerionhq/design-system';
import { grantsViewsFor, type GrantsView } from './grants-views';
import { useTranslation } from 'react-i18next';

type GrantsViewToggleProps = {
  // Active preset, or null once the user has toggled columns off any preset.
  view: GrantsView | null;
  onSelectView: (view: GrantsView) => void;
};

const getPosition = (
  index: number,
  total: number,
): 'first' | 'middle' | 'last' => {
  if (index === 0) {
    return 'first';
  }
  if (index === total - 1) {
    return 'last';
  }
  return 'middle';
};

/**
 * Two horizontal preset items — List (the default columns) and Audit — that
 * flip the visible column set. Uses the same ToggleGroup as the "Show" preset
 * chips above the table for a consistent look; per-column toggling is unchanged
 * and still done through the adjacent column-picker widget.
 */
export function GrantsViewToggle({
  view,
  onSelectView,
}: GrantsViewToggleProps) {
  const { t } = useTranslation('accessGrants');
  const { can } = useAccessControl();
  const views = grantsViewsFor(t, {
    canReview: can({ action: 'AccessGrant:Review' }),
  });

  return (
    <ToggleGroupRoot
      type="single"
      value={view ?? ''}
      onValueChange={(id) => {
        if (id) {
          onSelectView(id as GrantsView);
        }
      }}
    >
      {views.map((v, index) => (
        <ToggleGroupItem
          key={v.id}
          value={v.id}
          position={getPosition(index, views.length)}
          className="bg-white whitespace-nowrap dark:bg-gray-900"
          data-testid={`grants-view-${v.id}`}
        >
          {v.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroupRoot>
  );
}
